/*
 * Levels Beyond CONFIDENTIAL
 *
 * Copyright 2003 - 2020 Levels Beyond Incorporated
 * All Rights Reserved.
 *
 * NOTICE: All information contained herein is, and remains the
 * property of Levels Beyond Incorporated and its suppliers, if
 * any. The intellectual and technical concepts contained herein
 * are proprietary to Levels Beyond Incorporated and its suppliers
 * and may be covered by U.S. and Foreign Patents, patents in
 * process, and are protected by trade secret or copyright law.
 * Dissemination of this information or reproduction of this
 * material is unlawful and strictly forbidden unless prior
 * written permission is obtained from Levels Beyond Incorporated.
 */

/**
 * Filename: package-notifications-service.ts
 *
 * Created by randysorensen on 4/9/18.
 */

angular
    .module('prism-ui.packageNotifications')
    .service('PackageNotificationsService', function (
        this: JS5Object,
        _: JS5Object,
        $q: JS5Object,
        $location: JS5Object,
        $route: JS5Object,
        $routeParams: JS5Object,
        PackageNotificationsResourceFactory: JS5Object,
        NotificationService: JS5Object,
        EmailTemplatesResourceFactory: JS5Object)
    {

        this.packageStatusDictionary = {
            DRAFT: 'Draft',
            READY_FOR_DELIVERY: 'Ready for Delivery',
            RESTORING: 'Restoring',
            PENDING_RESTORE: 'Pending Restore',
            TRANSCODING: 'Creating Package Media',
            CREATING_CLIP: 'Creating Clip Media',
            PENDING_DELIVERY: 'Pending Delivery',
            DELIVERING: 'Delivering',
            PENDING_APPROVAL: 'Pending Approval',
            STALLED: 'Stalled',
            REJECTED: 'Rejected',
            DELIVERED: 'Delivered'
        };

        this.getPackageNotifications = () => {
            return PackageNotificationsResourceFactory.query().$promise;
        };

        this.createPackageNotification = (notification: JS5Object) => {
            return PackageNotificationsResourceFactory.save(notification).$promise;
        };

        this.getPackageNotification = (notificationId: number) => {
            return PackageNotificationsResourceFactory.get({
                notificationId: notificationId
            }).$promise;
        };

        this.updatePackageNotification = (notification: JS5Object) => {
            return PackageNotificationsResourceFactory.update(notification).$promise;
        };

        this.getPackageNotificationEditData = () => {
            let emailTemplatesPromise = EmailTemplatesResourceFactory.query().$promise;

            let availablePackageStatuses = <JS5Object>[];
            _.forEach(this.packageStatusDictionary, (label: string, status: string) => {
                availablePackageStatuses.push({
                    label: label,
                    status: status
                });
            });

            let packageNotificationPromise: JS5Promise;
            if ($routeParams.notificationId < 0) {
                let packageNotificationDeferral = $q.defer();
                packageNotificationDeferral.resolve({
                    recipients: []
                });
                packageNotificationPromise = packageNotificationDeferral.promise;
            } else {
                packageNotificationPromise = this.getPackageNotification($routeParams.notificationId);
            }

            return $q.all({
                emailTemplates: emailTemplatesPromise,
                availablePackageStatuses: availablePackageStatuses,
                packageNotification: packageNotificationPromise
            }).then((editData: JS5Object) => {
                this.notification = editData.packageNotification;
                this.originalNotification = _.cloneDeep(this.notification);
                return editData;
            });
        };

        this.canRevertEditView = () => {
            return _.get(this.editorForm, '$dirty') || $routeParams.notificationId < 0;
        };

        this.canExitEditView = () => {
            let notificationIsSaved = !!_.get(this.notification, 'id');
            return !this.editorForm || (notificationIsSaved && this.editorForm.$pristine);
        };

        const closeEditView = () => {
            this.notification = undefined;
            this.originalNotification = undefined;
            this.editorForm = undefined;

            $location.path($route.current.rootRoute);
        };

        this.showUnsavedChangesNotification = () => {
            NotificationService.createWarningNotification('You have unsaved changes. Please save or revert.', '');
        };

        this.exitEdit = () => {
            if (this.canExitEditView()) {
                closeEditView();
            } else {
                this.showUnsavedChangesNotification();
            }
        };

        this.revertEditData = () => {
            if ($routeParams.notificationId < 0) {
                closeEditView();
            } else {
                this.notification = _.cloneDeep(this.originalNotification);
                this.editorForm.$setPristine();
                return this.notification;
            }
        };

        this.saveEditData = () => {
            let saveFunction: JS5Function;

            if ($routeParams.notificationId < 0) {
                saveFunction = this.createPackageNotification;
            } else {
                saveFunction = this.updatePackageNotification;
            }

            return saveFunction(this.notification)
                .then((savedNotification: JS5Object) => {
                    this.notification = savedNotification;
                    this.originalNotification = _.cloneDeep(savedNotification);

                    $route.updateParams({
                        notificationId: savedNotification.id
                    });

                    this.editorForm.$setPristine();
                    return this.notification;
                }).catch((error: JS5Object) => {
                    NotificationService.createFailureNotification(
                        'An error occurred while saving the notification.',
                        error.data.message);
                });

        };

    });
